<script setup lang="ts">
import { computed } from "vue";
import { retrySystemHistory, useSettled, useSystemHistory } from "../composables/useHistory";
import { historyRange, RANGE_LABELS } from "../composables/useRange";
import { formatGrouped } from "../format";
import { METRICS, SERIES_COLORS, type MetricId } from "../metrics";
import { BUCKET_SECONDS, bucketAxis, fillAxis } from "../series";
import AppIcon from "./AppIcon.vue";
import RangeTabs from "./RangeTabs.vue";
import TimeChart, { type ChartSeries } from "./TimeChart.vue";

const props = defineProps<{ metric: MetricId }>();
defineEmits<{ close: [] }>();

const definition = computed(() => METRICS[props.metric]);
const metricNames = computed(() => definition.value.series.map((item) => item.metric));
const step = computed(() => BUCKET_SECONDS[historyRange.value]);
const entries = useSystemHistory(metricNames, historyRange, step);
/** What is on screen: the newest loaded history, which lags the selected range while it loads. */
const history = useSettled(() => ({
  ready: entries.value.every((entry) => entry.status === "ready"),
  error: entries.value.find((entry) => entry.status === "error")?.error ?? null,
  value: { entries: entries.value, range: historyRange.value }
}));

const chart = computed(() => {
  const shown = history.value.value;
  if (!shown) return null;
  const resolution = Math.max(...shown.entries.map((entry) => entry.resolution));
  const axis = bucketAxis(
    shown.entries.flatMap((entry) => entry.points.map((point) => point.timestamp)),
    resolution
  );
  const rows = shown.entries.map((entry) => fillAxis(axis, entry.points, (point) => point.value));

  // Link rates are stored in kbit/s; switch the whole chart to Mbit/s once that reads better.
  let { unit, digits } = definition.value;
  let factor = 1;
  if (unit === "kbit/s" && rows.some((row) => row.some((value) => value != null && value >= 1000))) {
    unit = "Mbit/s";
    digits = 2;
    factor = 1 / 1000;
  }
  const series: ChartSeries[] = definition.value.series.map((item, index) => ({
    id: item.metric,
    name: item.name,
    color: SERIES_COLORS[index],
    values: rows[index].map((value) => (value == null ? null : value * factor))
  }));
  return { axis, series, unit, digits, label: RANGE_LABELS[shown.range] };
});

/** A one-line reading of what the chart shows, for metrics with a single series. */
const summary = computed(() => {
  const current = chart.value;
  if (!current || current.series.length !== 1) return "";
  const values = current.series[0].values.filter((value): value is number => value != null);
  if (!values.length) return "";
  const { unit, digits } = current;
  const text = (value: number) => `${formatGrouped(value, digits)}${unit === "%" ? "%" : unit ? ` ${unit}` : ""}`;
  const total = values.reduce((sum, value) => sum + value, 0);
  const highest = Math.max(...values);
  if (definition.value.aggregate === "sum") return `${current.label}合计 ${text(total)}`;
  if (definition.value.aggregate === "max") return `${current.label}最高 ${text(highest)}`;
  return `${current.label}平均 ${text(total / values.length)}，最高 ${text(highest)}`;
});

function retry() {
  for (const name of metricNames.value) retrySystemHistory(name, historyRange.value, step.value);
}
</script>

<template>
  <div class="panel">
    <header class="panel__head">
      <div class="panel__title">
        <h3>{{ definition.title }}</h3>
        <p v-if="summary" class="num">{{ summary }}</p>
        <ul v-else-if="chart && chart.series.length > 1" class="panel__legend">
          <li v-for="item in chart.series" :key="item.id">
            <i :style="{ background: item.color }" aria-hidden="true"></i>{{ item.name }}
          </li>
        </ul>
      </div>
      <div class="panel__tools">
        <RangeTabs />
        <button class="icon-button" type="button" aria-label="收起图表" @click="$emit('close')">
          <AppIcon name="close" :size="16" />
        </button>
      </div>
    </header>

    <div v-if="history.error && !chart" class="panel__state" role="alert">
      <p>{{ history.error }}</p>
      <button class="text-link" type="button" @click="retry">重新读取</button>
    </div>
    <div v-else-if="!chart" class="panel__state" aria-live="polite">正在读取走势…</div>
    <TimeChart
      v-else
      :times="chart.axis.times"
      :step-ms="chart.axis.stepMs"
      :series="chart.series"
      :unit="chart.unit"
      :digits="chart.digits"
      :busy="history.pending"
      :label="`${definition.title} ${chart.label}走势`"
    />
  </div>
</template>

<style scoped>
.panel { display: grid; gap: 1rem; padding: 1.25rem 0 0.5rem; }
.panel__head { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-start; justify-content: space-between; }
.panel__title h3 { font-size: 0.9375rem; }
.panel__title p { margin-top: 0.15rem; color: var(--ink-3); font-size: 0.8125rem; }
.panel__legend { display: flex; flex-wrap: wrap; gap: 0.25rem 1rem; margin: 0.2rem 0 0; padding: 0; list-style: none; color: var(--ink-2); font-size: 0.8125rem; }
.panel__legend li { display: inline-flex; gap: 0.4rem; align-items: center; }
.panel__legend i { width: 0.8rem; height: 2px; border-radius: 1px; }
.panel__tools { display: flex; gap: 0.25rem; align-items: center; }
.panel__state {
  display: grid;
  gap: 0.5rem;
  place-content: center;
  justify-items: center;
  height: 220px;
  color: var(--ink-3);
  font-size: 0.875rem;
}
</style>
